import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, ShieldCheck } from "lucide-react";
import { roleLabel } from "@/lib/groupBy";
import { usePermissions } from "@/context/PermissionsContext";

export default function Settings() {
  const { user } = useAuth();
  const { labels } = usePermissions();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 6) return toast.error("Yeni şifre en az 6 karakter olmalı");
    if (form.next !== form.confirm) return toast.error("Yeni şifreler eşleşmiyor");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Şifreniz güncellendi");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Hesap Ayarları</h1>
        <p className="mt-1 text-sm text-muted-foreground">Profil bilgileriniz ve şifre değişikliği</p>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-5" data-testid="profile-card">
          <h3 className="flex items-center gap-2 font-heading text-lg font-semibold"><ShieldCheck className="h-5 w-5 text-primary" /> Profil</h3>
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-muted-foreground">Ad</dt><dd className="font-medium">{user?.name}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">E-posta</dt><dd className="font-medium" data-testid="profile-email">{user?.email}</dd></div>
            <p className="pt-2 text-xs text-muted-foreground" data-testid="profile-report-note">Günlük (23:00), haftalık (Pazartesi 09:00) ve aylık (ayın 1'i 09:00) satış ve stok özetleriniz bu e-posta adresine otomatik gönderilir.</p>
            <div className="flex justify-between"><dt className="text-muted-foreground">Rol</dt><dd><span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary" data-testid="profile-role">{roleLabel(user?.role, labels)}</span></dd></div>
          </dl>
        </div>
        <form onSubmit={submit} className="rounded-xl border border-border bg-card p-5" data-testid="change-password-form">
          <h3 className="flex items-center gap-2 font-heading text-lg font-semibold"><KeyRound className="h-5 w-5 text-primary" /> Şifre Değiştir</h3>
          <div className="mt-4 space-y-3">
            <div><Label className="text-xs">Mevcut Şifre</Label><Input type="password" data-testid="pw-current" value={form.current} onChange={set("current")} required className="mt-1" /></div>
            <div><Label className="text-xs">Yeni Şifre</Label><Input type="password" data-testid="pw-new" value={form.next} onChange={set("next")} required className="mt-1" /></div>
            <div><Label className="text-xs">Yeni Şifre (Tekrar)</Label><Input type="password" data-testid="pw-confirm" value={form.confirm} onChange={set("confirm")} required className="mt-1" /></div>
          </div>
          <Button type="submit" data-testid="pw-submit" disabled={loading} className="mt-4 w-full font-semibold">Şifreyi Güncelle</Button>
        </form>
      </div>
    </div>
  );
}
